import React, { useId, useRef, useState } from 'react';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { useFetchWrapper } from '../../../_helpers/fetchWrapper.js';
import { SWITCH_ERRORS } from '../../../_helpers/switchErrors.js';
import config from '../../../config.js';
import '../../../styles/StreamCta.css';

/**
 * The confirm sheet for a song picked on the stream page (ENG-697/701):
 * "Request <title>?" with an optional name (`POST /queue`), or, with
 * `switchEntryId`, the switch of that request
 * (`POST /bands/{id}/queue/entries/{entryId}/switch`). What the queue
 * refuses is shown here. It stands in for the song page (mockup 22,
 * ENG-709/710) until that exists.
 */
const SongRequestSheet = ({ bandId, song, switchEntryId = null, onCancel, onDone }) => {
  const { t } = useLocale();
  const fetchWrapperRef = useRef(useFetchWrapper());
  const nameId = useId();
  const [name, setName] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  const confirm = async () => {
    if (sending) return;
    setSending(true);
    setError(null);
    try {
      if (switchEntryId) {
        await fetchWrapperRef.current.post(`${config.apiBaseUrl}/bands/${bandId}/queue/entries/${switchEntryId}/switch`, { song_id: song.id });
      } else {
        await fetchWrapperRef.current.post(`${config.apiBaseUrl}/queue`, {
          band_id: Number(bandId),
          title: song.title,
          artist: song.artist,
          requested_by: name.trim(),
        });
      }
      setSent(true);
      onDone?.();
    } catch (err) {
      if (switchEntryId) {
        setError({ key: SWITCH_ERRORS[err?.code] ?? 'publicSongList.switch.errors.generic' });
      } else {
        // The queue answers in plain words (409 for a repeat it refuses).
        const message = typeof err?.message === 'string' ? err.message.trim() : '';
        setError(message && err?.status && message !== err.statusText ? { text: message } : { key: 'stream.crate.panel.confirm.failed' });
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="crate-confirm" role="dialog" aria-label={song.title} data-testid="crate-confirm">
      {sent ? (
        <>
          <p className="crate-confirm-done" role="status">
            {t(switchEntryId ? 'stream.crate.panel.confirm.switched' : 'stream.crate.panel.confirm.requested', { title: song.title })}
          </p>
          <div className="crate-confirm-actions">
            <button type="button" className="crate-confirm-cancel" onClick={onCancel}>{t('stream.crate.panel.confirm.ok')}</button>
          </div>
        </>
      ) : (
        <>
          <p className="crate-confirm-title">
            {t(switchEntryId ? 'stream.crate.panel.confirm.switchTo' : 'stream.crate.panel.confirm.request', { title: song.title, artist: song.artist })}
          </p>
          {!switchEntryId && (
            <>
              <label htmlFor={nameId} className="crate-confirm-label">{t('stream.crate.panel.confirm.nameLabel')}</label>
              <input id={nameId} className="crate-confirm-name" value={name} maxLength={50} onChange={(e) => setName(e.target.value)} />
            </>
          )}
          {error && <p className="crate-confirm-error" role="alert">{error.text ?? t(error.key)}</p>}
          <div className="crate-confirm-actions">
            <button type="button" className="crate-confirm-cancel" onClick={onCancel}>{t('stream.crate.panel.confirm.cancel')}</button>
            <button type="button" className="crate-confirm-send stream-cta" onClick={confirm} disabled={sending} data-testid="crate-confirm-send">
              {t(switchEntryId ? 'stream.crate.panel.confirm.switch' : 'stream.crate.panel.confirm.send')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SongRequestSheet;
